import { ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FileSaver from 'file-saver';
import { Box, Flex, Heading, Text, Icon, Button, useToast, Select } from '@chakra-ui/react';

import PageHeader from 'layout/pageHeader';
import MDropZone from 'components/molecules/MDropZone';
import icons from 'shared/icons';
import { useLazyExportClientAliasesQuery, useUploadClientAliasesMutation } from 'core/API/productsApi';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { EXCEL_FILE_TYPE } from 'shared/const';
import { linkStyle } from 'shared/styles';
import { useTranslation } from 'helpers/languages/translations';
import { IErrorUploadResponse } from '../types';
import DescriptionHtml from 'components/molecules/DescriptionHtml';

const AliasesUpload = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadMethod, setUploadMethod] = useState<'keep' | 'override'>('keep');

    const toast = useToast();
    const navigate = useNavigate();
    const translate = useTranslation();

    const [exportAliases] = useLazyExportClientAliasesQuery();
    const [uploadAliases, { isLoading }] = useUploadClientAliasesMutation();

    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };

    const goBack = () => navigate(-1);
    const getTemplate = async () => {
        try {
            const { data: res, isError } = await exportAliases({ isTemplate: true });
            if (isError || !res) throw new Error(translate('splits.upload.templateError') as string);

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(blob, translate('alias.upload.templateFileName') as string);
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const uploadAliasesFromExcel = async () => {
        try {
            const bodyFormData = new FormData();
            bodyFormData.append('excelFile', uploadedFile as File);
            bodyFormData.append('uploadMethod', uploadMethod);

            const res = await uploadAliases(bodyFormData);
            setUploadedFile(null);

            if ('error' in res) {
                const error = res.error as IErrorUploadResponse;
                if (error?.data?.missing_headers && error?.data?.missing_headers?.length) {
                    if (error?.data?.missing_headers?.includes('aliases')) {
                        throw new Error(translate('product.alias.error.missing_aliases') as string);
                    } else {
                        throw new Error(
                            translate('product.upload.error.missing_specific_headers', {
                                headers: error?.data?.missing_headers.join(', '),
                            }) as string,
                        );
                    }
                }
                throw new Error(translate('alias.upload.error') as string);
            }

            goBack();
            toast({
                title: translate('alias.upload.success'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };
    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;
        const handleChangeUploadMethod = (e: ChangeEvent<HTMLSelectElement>) => {
            setUploadMethod(e.target.value as 'keep' | 'override');
        };

        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start" mr={5}>
                <Text>{translate('alias.upload.section1')}</Text>
                <Text>
                    {translate('product.upload.recommendation', {
                        template: (
                            <Text
                                onClick={intentionalyFloatingPromiseReturn(getTemplate)}
                                as="span"
                                {...linkStyle}
                                mx={1}
                            >
                                {translate('product.upload.downloadTemplate')}
                            </Text>
                        ),
                    })}
                </Text>
                <Text>{translate('product.upload.method')}</Text>
                <Select mb={8} mt={2} bg="white" value={uploadMethod} onChange={handleChangeUploadMethod}>
                    <option value="keep">{translate('alias.upload.method.keep')} </option>
                    <option value="override">{translate('alias.upload.method.override')}</option>
                </Select>
                <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        onClick={intentionalyFloatingPromiseReturn(uploadAliasesFromExcel)}
                        isLoading={isLoading}
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('product.upload.apply')}
                    </Button>
                </Flex>
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader
                pageTitle={translate('alias.upload.header') as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        {translate('product.upload.uploadFile')}
                    </Heading>
                </Flex>
                <Flex direction="row">
                    {renderDescriptionSection()}
                    <DescriptionHtml h="fit-content" tradKey="alias.upload.description" />
                </Flex>
            </Box>
        </Box>
    );
};

export default AliasesUpload;
